// Intake asset manifest — lets a super-admin swap intake imagery from the
// dashboard without an intake deploy.
// See docs/INTAKE_PRD_ASSET_MANIFEST_READ_2026_09_27.md (rev 2) and the
// backend's BE_PRD_INTAKE_ASSET_CMS_RESPONSE.md §4.
//
//   GET ${REACT_APP_BACKEND_URL}/intake/assets
//     → { version, assets: { [key]: { url, alt, w, h } } }
//       Cache-Control: public, max-age=300, stale-while-revalidate=86400
//       fresh DB / nothing published → { version: null, assets: {} }
//
// Every image keeps its bundled file as the fallback, so this is invisible
// until someone publishes an override — and stays invisible if the endpoint
// is dark (404), slow, or unreachable. NOTHING here may block a render.
//
// Deliberately no `?flow=` (PRD §2). Two reasons: our flow keys are camelCase
// where the backend's are not (antiAging vs antiaging), and lab illustrations
// live under the `labs` flow which is in neither lab flow's union — so a
// per-flow fetch would silently drop them. The whole manifest is ≤ 89 small
// entries and is edge-cached for 300 s.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const EMPTY_MANIFEST = { version: null, assets: {}, preview: null };

const shape = (raw) => {
  if (!raw || typeof raw !== 'object') return EMPTY_MANIFEST;
  const assets = raw.assets && typeof raw.assets === 'object' ? raw.assets : {};
  return {
    version: raw.version ?? null,
    assets,
    preview: raw.preview && typeof raw.preview === 'object' ? raw.preview : null,
  };
};

const fetchManifest = async (previewId) => {
  if (!BACKEND_URL) return EMPTY_MANIFEST;
  const url = new URL(`${BACKEND_URL.replace(/\/+$/, '')}/intake/assets`);
  if (previewId) url.searchParams.set('preview', String(previewId));
  try {
    const res = await fetch(url.toString(), {
      // A preview deliberately serves unpublished content and is no-store on
      // the server; don't let a browser or proxy keep it either.
      cache: previewId ? 'no-store' : 'default',
    });
    if (!res.ok) return EMPTY_MANIFEST; // 404 = dark, and that is fine
    return shape(await res.json());
  } catch {
    return EMPTY_MANIFEST; // offline, CORS, malformed JSON — all the same here
  }
};

// Published manifest only. A preview response is NEVER stored: it contains
// unpublished images and must not leak into an ordinary patient session.
let cached = null;
let inflight = null;

export const loadAssets = (previewId = null) => {
  if (previewId) return fetchManifest(previewId);
  if (cached) return Promise.resolve(cached);
  if (!inflight) {
    inflight = fetchManifest(null).then((m) => {
      cached = m;
      inflight = null;
      return m;
    });
  }
  return inflight;
};

/** Test seam — also used if we ever need to force a refetch. */
export const __resetAssetCache = () => {
  cached = null;
  inflight = null;
};

/** `?assetPreview=<versionId>` from the dashboard's "View in intake" button. */
export const previewIdFromSearch = (search) => {
  try {
    const v = new URLSearchParams(search || '').get('assetPreview');
    return v && v.trim() ? v.trim() : null;
  } catch {
    return null;
  }
};

// ---------------------------------------------------------------- slot keys

/** previewMeta stores '<flow>/<name>'; the slot is 'illustration.<flow>.<name>'. */
export const illustrationKey = (image) =>
  typeof image === 'string' && image.includes('/')
    ? `illustration.${image.replace('/', '.')}`
    : null;

/** Landing program card → 'landing.card.<slug>'. */
export const landingCardKey = (slug) =>
  typeof slug === 'string' && slug ? `landing.card.${slug}` : null;

// ---------------------------------------------------------------- resolution

/** Published override for `key`, else the bundled path. Never throws. */
export const assetUrl = (assets, key, bundled = null) => {
  const hit = key && assets ? assets[key] : null;
  return (hit && typeof hit.url === 'string' && hit.url) || bundled;
};

/** Published alt for `key`, else the caller's fallback. */
export const assetAlt = (assets, key, fallback = null) => {
  const hit = key && assets ? assets[key] : null;
  const alt = hit && typeof hit.alt === 'string' ? hit.alt.trim() : '';
  return alt || fallback;
};
